/**
 * GlobalBulkOperationQueue
 *
 * This component renders the BulkOperationQueue globally based on API data.
 * It should be placed at a high level in the app (e.g., in PrivateRoutes)
 * and will automatically show/hide based on active bulk operations.
 *
 * It is also the single host for the automation underway rows (behind the
 * `feStageAutomation` flag): there is no separate automation popup — the
 * underway section is contributed here so everything lives in one popup.
 *
 * Usage:
 * ```tsx
 * import { BulkOperationsProvider, GlobalBulkOperationQueue } from '@/components/ats/BulkOperationQueue';
 *
 * // Wrap your app with the provider
 * <BulkOperationsProvider>
 *   <App />
 *   <GlobalBulkOperationQueue />
 * </BulkOperationsProvider>
 * ```
 */
import { useHistory } from 'react-router-dom';
import { useBreakpointValue } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  AutomationUnderwaySection,
  useUnderwaySummary,
  useUnderwayStore,
} from '@/components/automation/UnderwayPopup';
import { useBulkOperationsOptional } from './BulkOperationsContext';
import { BulkOperationQueue } from './BulkOperationQueue';
import type { BulkOperationQueueConfig } from './BulkOperationQueue.interface';

interface GlobalBulkOperationQueueProps {
  /** Position from the left edge of the screen */
  leftOffset?: string;
  /** Position from the bottom edge of the screen */
  bottomOffset?: string;
  /** Width of the component */
  width?: string;
}

export const GlobalBulkOperationQueue = ({
  leftOffset = '80px',
  bottomOffset = '0',
  width = '416px',
}: GlobalBulkOperationQueueProps) => {
  const history = useHistory();
  const bulkOps = useBulkOperationsOptional();
  const { feStageAutomation } = useKFlags();
  const underway = useUnderwaySummary();
  const clearTerminal = useUnderwayStore((s) => s.clearTerminal);

  const RECENT_ACTIVITIES_PATH = '/notifications/recent-activities';
  const VIEW_MODE = {
    HIDDEN: 'hidden',
    MINIMIZED: 'minimized',
    EXPANDED: 'expanded',
  };

  // Track if queue should open expanded - set by bulkActionStarted event
  const [openExpanded, setOpenExpanded] = useState(false);
  // Counter to force remount when state changes
  const [mountKey, setMountKey] = useState(0);

  // Check if mobile view - don't render on mobile
  const isMobile = useBreakpointValue({ base: true, md: false });

  const isBulkActionsVisible = bulkOps?.isBulkActionsVisible ?? false;

  // Automation has visible work to host inside this popup.
  const automationActive = feStageAutomation && underway.hasActivities;

  // Open expanded when a bulk action starts OR a new automation row is
  // enqueued (`automationEnqueued` is dispatched by useEnqueueAutomationMove).
  // setState lives in the event handler — not an effect body — so this
  // mirrors the existing pattern without a cascading-render lint violation.
  useEffect(() => {
    const handleActivityStarted = () => {
      setOpenExpanded(true);
      setMountKey((k) => k + 1);
    };
    window.addEventListener('bulkActionStarted', handleActivityStarted);
    window.addEventListener('automationEnqueued', handleActivityStarted);
    return () => {
      window.removeEventListener('bulkActionStarted', handleActivityStarted);
      window.removeEventListener('automationEnqueued', handleActivityStarted);
    };
  }, []);

  // When bulk actions become visible (queue will hide), prepare for minimized on next show
  useEffect(() => {
    if (isBulkActionsVisible) {
      queueMicrotask(() => {
        setOpenExpanded(false);
        setMountKey((k) => k + 1);
      });
    }
  }, [isBulkActionsVisible]);

  // Don't render when used outside BulkOperationsProvider
  if (!bulkOps) {
    return null;
  }

  const { config, viewMode, minimizeToSidebar, markActivitiesAsViewed } =
    bulkOps;

  // Environmental gates — apply to the whole popup (bulk + automation).
  if (isMobile) {
    return null;
  }
  // Don't render on the recent activities page (it already shows all activity
  // info) — EXCEPT while an automation action is running, so its live underway
  // progress stays visible there too.
  if (
    history.location.pathname === RECENT_ACTIVITIES_PATH &&
    !automationActive
  ) {
    return null;
  }
  // When table bulk actions are visible, hide the queue
  if (isBulkActionsVisible) {
    return null;
  }

  // Content gates — render when there is bulk work OR automation work.
  if (!config && !automationActive) {
    return null;
  }
  // Bulk hide/minimize is only honoured when automation isn't active.
  // (Automation active ⇒ visible, locally minimizable via the header.)
  if (
    !automationActive &&
    (viewMode === VIEW_MODE.HIDDEN || viewMode === VIEW_MODE.MINIMIZED)
  ) {
    return null;
  }

  // Mark activities as viewed (entity_views) then navigate to recent activities when expand is clicked
  const handleExpand = async () => {
    await markActivitiesAsViewed();
    history.push(RECENT_ACTIVITIES_PATH);
  };

  // Synthesize a minimal config when automation rows exist but there are no
  // bulk operations, so the popup can still host the automation section.
  const effectiveConfig: BulkOperationQueueConfig = config ?? {
    groups: [],
    progress: underway.progress,
    showExpandButton: true,
    showCloseButton: true,
  };

  return (
    <BulkOperationQueue
      key={mountKey}
      config={{
        ...effectiveConfig,
        // When a bulk operation just started, open expanded
        // Otherwise, open minimized
        defaultMinimized: !openExpanded,
        onClose: minimizeToSidebar,
        onExpand: handleExpand,
      }}
      extraSection={
        automationActive ? <AutomationUnderwaySection /> : undefined
      }
      extraInProgressCount={automationActive ? underway.inFlightCount : 0}
      onCloseExtra={feStageAutomation ? clearTerminal : undefined}
      // Only raise the popup above drawers/modals when the automation underway
      // rows are actually hosted here; plain bulk operations keep their legacy
      // stacking, so behaviour without stage automation is unchanged.
      elevated={automationActive}
      leftOffset={leftOffset}
      bottomOffset={bottomOffset}
      width={width}
    />
  );
};
